/**
 * External dependencies
 */
import clsx from 'clsx';

/**
 * WordPress dependencies
 */
import { forwardRef, useRef } from '@wordpress/element';

/**
 * Internal dependencies
 */
import { ControlWithError } from '../control-with-error';
import ContentEditableControl from '../../content-editable-control';

/**
 * A `contentEditable` field does not participate in the Constraint Validation
 * API, so — like `ValidatedToggleGroupControl` — the validity state lives on a
 * visually hidden delegate input that mirrors whether the field has content.
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const UnforwardedValidatedContentEditableControl = ({
  required,
  customValidity,
  markWhenOptional,
  value,
  className,
  ...restProps
}, forwardedRef) => {
  const validityTargetRef = useRef(null);
  return /*#__PURE__*/_jsxs("div", {
    className: "components-validated-control__wrapper-with-error-delegate",
    children: [/*#__PURE__*/_jsx(ControlWithError, {
      required: required,
      markWhenOptional: markWhenOptional,
      customValidity: customValidity,
      getValidityTarget: () => validityTargetRef.current,
      children: /*#__PURE__*/_jsx(ContentEditableControl, {
        ref: forwardedRef,
        className: clsx('components-validated-control__content-editable', className),
        "aria-invalid": customValidity?.type === 'invalid' || undefined,
        ...restProps
      })
    }), /*#__PURE__*/_jsx("input", {
      className: "components-validated-control__error-delegate",
      type: "text",
      ref: validityTargetRef,
      required: required
      // Whitespace-only content reads as empty, matching how a
      // text-oriented form field is expected to validate `required`.
      ,
      value: value?.trim() ?? '',
      tabIndex: -1,
      onChange: () => {},
      onFocus: e => {
        e.target.previousElementSibling?.querySelector('[role="textbox"]')?.focus();
      }
    })]
  });
};
export const ValidatedContentEditableControl = forwardRef(UnforwardedValidatedContentEditableControl);
ValidatedContentEditableControl.displayName = 'ValidatedContentEditableControl';